import { Clock, AlertTriangle } from "lucide-react";
import { ROLE_LABEL } from "../../lib/roleOptions";

/**
 * "Stage 1 of 2 — awaiting verification by Pankaj Kumar", on a claim still in
 * flight. Lives in common/ because every two-stage module renders it —
 * expenses, leaves and tour plans alike (it started expenses-only, which is
 * why an in-flight leave used to say nothing at all).
 *
 * STAGE 1 VERIFIES, STAGE 2 APPROVES (B2): the wording names which signature
 * a claim is waiting for, not just whose queue it is in. Single-stage claims
 * keep the plain "waiting on".
 *
 * PASSIVE DISPLAY. No action attaches to this line. (The bell is real now —
 * RC-6's event feed notifies the same people this line names, resolved by the
 * same server function — but THIS component still only displays.)
 *
 * **EVERY FACT HERE COMES FROM THE SERVER.** The stage, the total, and the
 * approver's NAME are all computed by `team_scope.waiting_on`, which derives
 * them from the approval routing itself and confirms each name with
 * `can_approve_submission`. Nothing in this file works out who approves what —
 * resolving that on the client would be a second source of truth for the one
 * rule this codebase has watched drift three times, and #78 shipped when two
 * such lists disagreed. The only thing done here is turning a role KEY into a
 * human label, which is presentation and not routing.
 *
 * `record.waiting_on` is absent on decided rows (the server stamps only
 * pending ones), so absence renders nothing. An empty `approvers` on a stamped
 * row is the opposite: a real answer meaning nobody can act, which gets said
 * out loud rather than hidden.
 */
export default function WaitingOn({ record, className = "" }) {
  const w = record?.waiting_on;
  if (!w) return null;

  // "Stage 1 of 2", or bare "Stage 1" when the server could not resolve the
  // subject's chain — never a guessed total that would look like a real answer.
  const position =
    w.total_stages ? `Stage ${w.stage} of ${w.total_stages}` : `Stage ${w.stage}`;

  // Which SIGNATURE is awaited. Only a two-stage chain has the verification /
  // final-approval split; anything else keeps the neutral wording.
  const awaiting =
    w.total_stages === 2
      ? w.stage === 1
        ? "awaiting verification by"
        : "awaiting final approval by"
      : "waiting on";

  const approvers = w.approvers || [];

  if (approvers.length > 0) {
    return (
      <p
        className={`text-[11px] leading-tight text-callisto-muted flex items-center gap-1 ${className}`}
        data-testid={`waiting-on-${record.id}`}
      >
        <Clock size={11} className="shrink-0" />
        <span>
          {position} — {awaiting}{" "}
          <span className="font-semibold text-callisto-navy">
            {approvers.map((a) => a.name).join(", ")}
          </span>
        </span>
      </p>
    );
  }

  // NOBODY CAN ACT. The released-officer case: an officer with no manager is in
  // no Sales Manager's team, so stage 1 resolves to nobody and the claim sits
  // indefinitely. Shown loudly and specifically — the role is named when the
  // server could name it, so this reads "no Sales Manager assigned" rather than
  // a shrug. Blank here would hide the one thing worth surfacing.
  const role = w.approver_role ? ROLE_LABEL[w.approver_role] : null;
  return (
    <p
      className={`text-[11px] leading-tight text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1 ${className}`}
      data-testid={`waiting-on-nobody-${record.id}`}
    >
      <AlertTriangle size={11} className="shrink-0" />
      <span>
        {position} —{" "}
        {role
          ? `no ${role} assigned, so nobody can approve this yet`
          : "no approver could be determined for this claim"}
      </span>
    </p>
  );
}
